import type { PredictorConfig } from "../engine/config";
import type { IsoDateTime } from "../engine/dates";
import { autoTune, type TuneOutcome } from "../engine/autoTune";
import type { ReplayInput } from "../engine/replay";
import { classify } from "../engine/predictor";
import { effectiveConfig } from "../engine/config";
import type { Shot } from "../engine/model";
import { TUNING_LAST_RUN_AT, TUNING_LAST_RUN_SHOTS, type Repositories } from "../storage/repositories";
import { appendVersion } from "./activeConfig";
import { activeShots } from "./calibration";

/**
 * When and how automatic tuning runs on the phone.
 *
 *  - First run once the phone has TUNE_FIRST_AT valid shots, then after every TUNE_EVERY further valid shots. A run
 *    that changes nothing still counts as a run.
 *  - The replay sees active shots only (nothing before a grinder's recalibration), the most recent TUNE_MAX_SHOTS of
 *    them, so a long history never makes it slow.
 *  - The search itself runs off the main thread (see tuneWorker.ts); `runner` is injectable so tests run it inline.
 */
export const TUNE_FIRST_AT = 40;
export const TUNE_EVERY = 20;
export const TUNE_MAX_SHOTS = 300;

export type TuneRunner = (input: ReplayInput, cfg: PredictorConfig) => Promise<TuneOutcome>;

export const inlineRunner: TuneRunner = async (input, cfg) => autoTune(input, cfg);

export interface TuningStatus {
  readonly validShots: number;
  readonly lastRunShots: number | null;
  readonly due: boolean;
  /** valid shots still to log before the next check */
  readonly shotsUntilNext: number;
}

export async function countValidShots(repos: Repositories, cfg: PredictorConfig): Promise<number> {
  const beans = new Map((await repos.beans.findAll()).map((b) => [b.id, b]));
  let n = 0;
  for (const s of await repos.shots.findAll()) {
    const c = effectiveConfig(cfg, beans.get(s.beanId) ?? null);
    if (classify((s.timeSeconds * c.targetYieldG) / s.yieldG, c) === "valid") n++;
  }
  return n;
}

export async function tuningStatus(repos: Repositories, cfg: PredictorConfig): Promise<TuningStatus> {
  const validShots = await countValidShots(repos, cfg);
  const raw = await repos.settings.get(TUNING_LAST_RUN_SHOTS);
  const lastRunShots = raw === null ? null : Number(raw);
  const nextAt = lastRunShots === null ? TUNE_FIRST_AT : Math.max(TUNE_FIRST_AT, lastRunShots + TUNE_EVERY);
  return { validShots, lastRunShots, due: validShots >= nextAt, shotsUntilNext: Math.max(0, nextAt - validShots) };
}

export async function replayInput(repos: Repositories): Promise<ReplayInput> {
  const grinders = await repos.grinders.findAll();
  const byId = new Map(grinders.map((g) => [g.id, g]));
  const all = (await repos.shots.findAll()).filter((s) => {
    const g = byId.get(s.grinderId);
    return g !== undefined && activeShots([s], g).length === 1;
  });
  const shots: Shot[] = all.slice(0, TUNE_MAX_SHOTS).reverse(); // findAll is newest first
  const used = new Set(shots.map((s) => s.beanId));
  const beans = (await repos.beans.findAll()).filter((b) => b.id !== null && used.has(b.id)).sort((a, b) => (a.id as number) - (b.id as number));
  return { grinders, beans, shots, offsets: await repos.offsets.load() };
}

/**
 * Run tuning if it is due. Adopted changes become a new active config version (with before/after scores).
 * Returns the outcome, or null when it was not due.
 */
export async function runTuningIfDue(repos: Repositories, cfg: PredictorConfig, now: IsoDateTime,
                                     runner: TuneRunner = inlineRunner): Promise<TuneOutcome | null> {
  const status = await tuningStatus(repos, cfg);
  if (!status.due) return null;
  const outcome = await runner(await replayInput(repos), cfg);
  if (outcome.changed) await appendVersion(repos, "tuned", outcome.after, now, { before: outcome.scoreBefore, after: outcome.scoreAfter });
  await repos.settings.put(TUNING_LAST_RUN_SHOTS, String(status.validShots));
  await repos.settings.put(TUNING_LAST_RUN_AT, now);
  return outcome;
}
